import React from "react";
import {Card} from "react-bootstrap";
import HeaderWithHelp from "../SalesAnalysis/HeaderWithHelp.jsx";
import {kwLabel} from "./KwSelect.jsx";
import {formatDatum, formatMenge} from "./format.js";

/**
 * Getrennter Kasten "Cosmos-Pufferbestand" (PLAN.md Abschnitt 5/F2, seit
 * Auftrag #236 aus der importierten Cosmos-Liste statt aus dem Weclapp-Lager
 * 2350209): Summe, Zeilenzahl, Stand-Datum der Liste und Quelle - deutlich
 * als "brutto, enthält offene Bestellungen, nicht in der Rechnung"
 * ausgewiesen, damit niemand ihn mit dem gerechneten SNV-Bestand
 * verwechselt. Zusätzlich rein informativer Hinweis auf laufende
 * Puffer-Produktionen (Meeting 22.09.2026, Punkt 8) - fließt NICHT in die
 * Rechnung ein, nur Anzeige.
 */
export default function CosmosPufferKasten({puffer}) {
    if (!puffer || !puffer.artikel) {
        return null;
    }

    const produktionenLaufend = puffer.produktionen_laufend || [];
    const ohneSichereZuordnung = (puffer.nicht_zugeordnet || 0) + (puffer.dubletten || 0) + (puffer.klaerung || 0);
    const jeMarke = Object.entries(puffer.je_marke || {});

    return (
        <Card className="shadow-sm mb-3 border-warning-subtle">
            <Card.Body className="py-2 px-3 d-flex flex-wrap align-items-center gap-3">
                <span className="fw-semibold">
                    <HeaderWithHelp help="Bestand bei Cosmos laut der Excel-Liste „Cosmos Stock Overview“, die im Ordermanagement importiert wird. Das Weclapp-Lager „Cosmos“ hat keine echten Daten und wird hier nicht gelesen.">
                        Cosmos-Pufferbestand
                    </HeaderWithHelp>
                </span>
                <span>
                    <HeaderWithHelp help="Summe der Spalte STOCK der Cosmos-Liste. Brutto: offene Bestellungen (Reservierungen) sind in der Menge enthalten und von Cosmos nicht getrennt ausgewiesen - verfügbar ist also weniger.">
                        {formatMenge(puffer.summe)} Dosen
                    </HeaderWithHelp>
                </span>
                {jeMarke.length > 0 && (
                    <span className="text-muted small">
                        <HeaderWithHelp help="Aufteilung der Summe nach den beiden Blättern der Cosmos-Liste, ebenfalls brutto.">
                            {jeMarke.map(([marke, menge]) => `${marke} ${formatMenge(menge)}`).join(" · ")}
                        </HeaderWithHelp>
                    </span>
                )}
                <span className="text-muted">
                    <HeaderWithHelp help="Anzahl der Artikelzeilen (Farbtöne und Sonderartikel) der Cosmos-Liste.">{formatMenge(puffer.artikel)} Zeilen</HeaderWithHelp>
                </span>
                {puffer.stand_am && (
                    <span className="text-muted">
                        <HeaderWithHelp help="Stand der Cosmos-Liste (Datum aus dem Dateinamen). Cosmos liefert sie derzeit monatlich, jede Liste wird als eigene Momentaufnahme gespeichert.">
                            Stand {formatDatum(puffer.stand_am)}
                        </HeaderWithHelp>
                    </span>
                )}
                {puffer.quelle && (
                    <span className="text-muted">
                        <HeaderWithHelp help="Herkunft der Zahl: die von Cosmos per Mail gelieferte Excel-Liste, hier hochgeladen - keine Abfrage bei Weclapp.">
                            Quelle: {puffer.quelle}
                        </HeaderWithHelp>
                    </span>
                )}
                {ohneSichereZuordnung > 0 && (
                    <span className="text-danger-emphasis">
                        <HeaderWithHelp help="Zeilen der Liste ohne sichere Weclapp-Artikelnummer: nicht zuordenbare Codes, doppelte Codes und Klärungsfälle. Ihre Menge steckt in der Summe; die Einzelheiten stehen auf der Seite „Quellen und Berechnungslogik“.">
                            {formatMenge(ohneSichereZuordnung)} ohne sichere Zuordnung
                        </HeaderWithHelp>
                    </span>
                )}
                {produktionenLaufend.length > 0 && (
                    <span className="text-muted">
                        <HeaderWithHelp help="Noch nicht abgeschlossene Produktionen für dieses Pufferlager – Kapitalbindung, kein SNV-Zulauf, siehe Lieferungen-Liste.">
                            Laufend:
                        </HeaderWithHelp>{" "}
                        {produktionenLaufend
                            .map((p) => `${kwLabel(p.kw) || "KW offen"} (${formatMenge(p.menge)} Stück)`)
                            .join(" · ")}
                    </span>
                )}
                <span className="text-warning-emphasis ms-auto">{puffer.hinweis}</span>
            </Card.Body>
        </Card>
    );
}
